import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  ResponsiveImage,
  Modal,
  Icons,
} from '../../../components';
import './image-gallery.css';
import css from './ListingImageGallery.module.css';
import { isArrayLength } from '../../../util/genericHelpers';
import { getFirstAvailableVariant, getViewerImage } from './imageHelpers';
import VirtualTourCardBadge from '../../../components/listing/VirtualTourCardBadge';
import { scrollToVirtualTour } from '../virtualTourNavigation';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  // Listing pages can contain dozens of high-resolution images. Rendering every
  // slide at once makes high-density mobile screens decode the 2x variant for
  // every photo, which can exhaust the browser's image memory and blank the
  // active slide. react-image-gallery still preloads the neighbouring slides.
  lazyLoad: true,
};

const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, variantNames) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = getFirstAvailableVariant(firstImage, variantNames);
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = ({
  className,
  rootClassName,
  images,
  imageVariants,
  thumbnailVariants,
  previewVariants,
  viewport,
  hasVirtualTour = false,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [isZoomModalOpen, setZoomModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [Viewer, setViewer] = useState(null);
  const intl = useIntl();
  const largeImageRefs = useRef([]);
  const isMobileLayout = viewport?.width > 0 && viewport?.width < 1024;
  const thumbVariants = thumbnailVariants || imageVariants;
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('react-viewer').then(module => {
        setViewer(() => module.default);
      });
    }
  }, []);

  // react-viewer resets to activeIndex whenever its images array changes.
  // Keep it stable during unrelated page updates, including while zoomed in.
  const viewerImages = useMemo(() => {
    const viewerVariants =
      Array.isArray(previewVariants) && previewVariants.length > 0
        ? previewVariants
        : [...(imageVariants || [])].reverse();
    return images.map((img, i) =>
      getViewerImage(
        img,
        intl.formatMessage(
          { id: 'ListingImageGallery.imageAltText' },
          { index: i + 1, count: images.length }
        ),
        viewerVariants
      )
    );
  }, [images, imageVariants, previewVariants, intl]);

  const items = images.map((img, i) => {
    return {
      index: i,
      original: '',
      alt: viewerImages[i].alt,
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: getFirstAvailableVariant(img, thumbVariants),
      galleryImage: getFirstAvailableVariant(img, imageVariants),
      viewerImage: viewerImages[i],
      image: img,
    };
  });

  const visibleItems = items.length > 5 ? items.slice(0, 5) : items;

  const renderItem = item => {
    // Native mobile fullscreen keeps its swipe controls. Upgrade only the
    // selected slide and its neighbours, even if many previews were visited.
    const distance = Math.abs(item.index - selectedImageIndex);
    const useViewerSource = isFullscreen && (distance <= 1 || distance === items.length - 1);
    return (
      <AspectRatioWrapper
        width={aspectWidth}
        height={aspectHeight}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        onClick={() => {
          setSelectedImageIndex(item.index);
          setZoomModalOpen(true);
        }}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={!useViewerSource && item.galleryImage?.url ? item.image : null}
            imageUrl={useViewerSource ? item.viewerImage.src : item.galleryImage?.url}
            alt={item.alt}
            variants={!useViewerSource && item.galleryImage?.url ? imageVariants : []}
            sizes={
              isFullscreen
                ? undefined
                : '(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px'
            }
          />
        </div>
      </AspectRatioWrapper>
    );
  };

  const renderThumbInner = item => (
    <div className={css.thumbWrapper}>
      <ResponsiveImage
        rootClassName={css.thumb}
        image={item.image}
        alt={item.thumbAlt}
        variants={thumbVariants}
        sizes="88px"
      />
    </div>
  );

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div>
      {isMobileLayout ? (
        <div className={css.mobileGallery} data-testid="mobile-gallery-container">
          {hasVirtualTour && !isFullscreen ? (
            <VirtualTourCardBadge
              context="listing"
              placement="top-left"
              onActivate={scrollToVirtualTour}
            />
          ) : null}
          <ReactImageGallery
            additionalClass={classes}
            items={items}
            renderItem={renderItem}
            renderThumbInner={renderThumbInner}
            onScreenChange={setIsFullscreen}
            onBeforeSlide={setSelectedImageIndex}
            disableKeyDown={isZoomModalOpen}
            renderLeftNav={(onClick, disabled) => (
              <button className={css.navLeft} disabled={disabled} onClick={onClick}>
                <div className={css.navArrowWrapper}>
                  <Icons name="leftAngle" />
                </div>
              </button>
            )}
            renderRightNav={(onClick, disabled) => (
              <button className={css.navRight} disabled={disabled} onClick={onClick}>
                <div className={css.navArrowWrapper}>
                  <Icons name="rightAngle" />
                </div>
              </button>
            )}
            renderFullscreenButton={(onClick, isFullscreen) =>
              isFullscreen ? (
                <Button
                  onClick={onClick}
                  rootClassName={css.close}
                  title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
                >
                  <span className={css.closeText}>
                    <FormattedMessage id="ListingImageGallery.closeModal" />
                  </span>
                  <IconClose rootClassName={css.closeIcon} />
                </Button>
              ) : (
                <button className={css.openFullscreen} onClick={onClick}>
                  <FormattedMessage
                    id="ListingImageGallery.viewImagesButton"
                    values={{ count: images.length }}
                  />
                </button>
              )
            }
            {...IMAGE_GALLERY_OPTIONS}
          />
        </div>
      ) : (
        <div
          className={classNames(css.gallaryDesktopSec, {
            [css.singleImageWrapper]: items.length === 1,
            [css.doubleImageWrapper]: items.length === 2,
            [css.trippleImageWrapper]: items.length === 3,
            [css.quadrupleImageWrapper]: items.length === 4,
          })}
        >
          {isArrayLength(visibleItems) &&
            visibleItems.map((item, index) => (
              <div
                key={index}
                className={css.itemWrapper}
                onClick={() => {
                  setSelectedImageIndex(index);
                  setZoomModalOpen(true);
                }}
              >
                {hasVirtualTour && index === 0 ? (
                  <VirtualTourCardBadge
                    context="listing"
                    placement="top-left"
                    onActivate={scrollToVirtualTour}
                  />
                ) : null}
                <img
                  src={item?.thumbnail?.url || item.image?.attributes?.variants?.['default']?.url}
                  alt={intl.formatMessage({ id: 'SectionHero.imageAlt1' })}
                />
              </div>
            ))}
          <Button className={css.viewPhotos} onClick={() => setShowAll(true)}>
            {intl.formatMessage({ id: 'ListingImageGallery.showAll' })} ({items?.length})
          </Button>
        </div>
      )}

      {isZoomModalOpen && Viewer && (
        <Viewer
          // Refit the current photo when the viewport changes, without changing its index.
          key={`${viewport?.width}:${viewport?.height}`}
          visible={isZoomModalOpen}
          noNavbar
          zIndex={9999}
          activeIndex={selectedImageIndex}
          onChange={(_, index) => setSelectedImageIndex(index)}
          onClose={() => setZoomModalOpen(false)}
          images={viewerImages}
          rotatable={false}
          scalable
          toolbar={['zoomIn', 'zoomOut', 'prev', 'next', 'reset', 'close']}
          className={css.toolBarContainer}
          zoomSpeed={0.4}
          drag
          zoomable
          downloadable={false}
        />
      )}

      <Modal
        usePortal
        id="ListingImageGallery"
        modalTitle={`View All (${items?.length})`}
        isOpen={showAll}
        onClose={() => setShowAll(false)}
        onManageDisableScrolling={() => {}}
        isGalleryModal={true}
      >
        <div className={css.modalGallery}>
          <h2>{intl.formatMessage({ id: 'ListingImageGallery.photoTour' })}</h2>
          <div className={css.smallImagesWrapper}>
            {isArrayLength(items) &&
              items.map((item, index) => (
                <div
                  className={css.smallThumbnailImages}
                  key={index}
                  onClick={() => {
                    largeImageRefs.current[index]?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }}
                >
                  <div className={css.smallThumbnailGrid}>
                    <img
                      src={
                        item?.thumbnail?.url || item.image?.attributes?.variants?.['default']?.url
                      }
                      alt={item.alt}
                    />
                  </div>
                  <div className={css.smallThumbnailInfos}>{`Photo ${index + 1}`}</div>
                </div>
              ))}
          </div>
          <div className={css.largeImages}>
            {isArrayLength(items) &&
              items.map((item, index) => (
                <div
                  className={css.categoryImages}
                  key={index}
                  ref={el => (largeImageRefs.current[index] = el)}
                >
                  <div className={css.categoryImagesTitle}>
                    {intl.formatMessage({ id: 'ListingImageGallery.imageTitle' })} {index + 1}
                  </div>
                  <div
                    onClick={() => {
                      setSelectedImageIndex(index);
                      setZoomModalOpen(true);
                    }}
                  >
                    <div className={css.smallThumbnailGrid}>
                      <ResponsiveImage
                        image={item.image}
                        alt={item.alt}
                        variants={imageVariants}
                        sizes="(max-width: 1024px) 100vw, 70vw"
                      />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ListingImageGallery;
